import React, { useRef, useState, useEffect } from "react"
import PropTypes from "prop-types"
import Map, { Marker, NavigationControl, Popup } from "react-map-gl";
import mapboxgl from "mapbox-gl";

const CompanyMap = (params) => {
  mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;
  const mapboxStyle = "mapbox://styles/mapbox/streets-v9";
  const mapboxAccessToken = "pk.eyJ1IjoiZGV2c3BhY2VvbmUiLCJhIjoiY2tyMXI0N3U4MjRsbTJ2cWFlOWR1OGM0NCJ9.hBD-m-8DIlw9dMKYpAs-zQ";
  const accountId = params.account_id;
  const companyId = params.company_id;
  const groups = params.groups;
  const mapRef = useRef();

  const [completedDates, setCompletedDates] = useState(params.completed_dates || []);
  const [mapLocations, setMapLocations] = useState(null);
  const [mapJobs, setMapJobs] = useState(null);
  const [mapViewState, setMapViewState] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [activeTab, setActiveTab] = useState("locations");

  useEffect(() => {
    loadLocations();
  }, [accountId, companyId]);

  useEffect(() => {
    fitToMarkers();
  }, [mapLocations, mapJobs]);

  function mergeCompletedDates(dates) {
    const result = dates || [];
    if (selectedDate && !result.includes(selectedDate)) {
      return [...result, selectedDate];
    }
    return result;
  }

  function loadLocations(groupId = selectedGroup) {
    if (!accountId || !companyId) return;

    clearSelection();
    clearMap();

    const query = groupId ? `?group_id=${groupId}` : "";
    fetch(`/accounts/${accountId}/companies/${companyId}/map_locations${query}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      }
    )
      .then(response => response.json())
      .then(data => {
        setMapLocations(data.locations || []);
        setCompletedDates(mergeCompletedDates(data.completed_dates));
      })
  }

  function loadJobs(date, groupId = selectedGroup) {
    clearSelection();
    clearMap();

    if (!accountId || !companyId || !date) return;

    const groupQuery = groupId ? `&group_id=${groupId}` : "";
    fetch(`/accounts/${accountId}/companies/${companyId}/map_jobs?date=${date}${groupQuery}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      }
    )
      .then(response => response.json())
      .then(data => {
        setMapJobs(data.jobs || []);
        setMapLocations(data.locations || []);
        setCompletedDates(mergeCompletedDates(data.completed_dates));
      })
  }

  function selectLocation(location) {
    if (selectedLocation && selectedLocation.id === location.id) {
      clearSelection();
      return;
    }
    setSelectedLocation(location);
    setSelectedJob((mapJobs || []).find((job) => job.location_id === location.id) || null);
  }

  function selectJob(job) {
    if (selectedJob && selectedJob.id === job.id) {
      clearSelection();
      return;
    }
    setSelectedJob(job);
    setSelectedLocation((mapLocations || []).find((location) => location.id === job.location_id) || null);
  }

  function clearMap() {
    setMapJobs([]);
    setMapLocations([]);
  }

  function clearSelection() {
    setSelectedLocation(null);
    setSelectedJob(null);
  }

  function handleDateChange(event) {
    const date = event.target.value;
    setSelectedDate(date);
    clearSelection();
    if (date) {
      loadJobs(date);
    } else {
      setSelectedJob(null);
      loadLocations();
    }
  }

  function handleGroupChange(event) {
    const groupId = event.target.value;
    setSelectedGroup(groupId);
    clearSelection();
    if (selectedDate) {
      loadJobs(selectedDate, groupId);
    } else {
      loadLocations(groupId);
    }
  }

  function fitToMarkers() {
    if (!mapRef.current) return;

    const bounds = new mapboxgl.LngLatBounds();
    if (mapLocations) {
      mapLocations
        .filter((loc) => loc.longitude != null && loc.latitude != null)
        .forEach((loc) => bounds.extend([loc.longitude, loc.latitude]));
    }
    if (mapJobs) {
      mapJobs
        .filter((job) => job.longitude != null && job.latitude != null)
        .forEach((job) => bounds.extend([job.longitude, job.latitude]));
    }
    if (bounds.isEmpty()) return;

    mapRef.current.fitBounds(bounds, { padding: 100, maxZoom: 15, animate: false });
  }

  function renderMarkers() {
    if (!mapLocations) return null;
    const markers = mapLocations
      .filter((location) => location.longitude != null && location.latitude != null)
      .map((location, index) => {
        return <Marker key={index} longitude={location.longitude} latitude={location.latitude} anchor="bottom" onClick={(e) => { e.originalEvent.stopPropagation(); selectLocation(location); }} />;
      });
    return (
      <>{markers}</>
    );
  }

  function renderJobMarkers() {
    if (!mapJobs) return null;
    const markers = mapJobs
      .filter((job) => job.longitude != null && job.latitude != null)
      .map((job, index) => {
        return (
          <Marker key={`job-${index}`} longitude={job.longitude} latitude={job.latitude} anchor="center" onClick={(e) => { e.originalEvent.stopPropagation(); selectJob(job); }}>
            <div style={{ width: 20, height: 20, backgroundColor: job.progress_color, borderRadius: "50%", border: "1px solid black" }} />
          </Marker>
        );
      });
    return <>{markers}</>;
  }

  return (
    <div>
      <div className="map-sidebar-container">
        <div className="pt-2 pr-2 small text-right">
          <a onClick={fitToMarkers} href="javascript:void(0)">Fit Map to Markers</a>
        </div>
        {completedDates.length > 0 && (
          <div className="p-2">
            <div>
              <select className="form-control" value={selectedDate} onChange={handleDateChange}>
                <option value="">Select date...</option>
                {completedDates.map((date, index) => (
                  <option key={index} value={date}>{date}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {groups && groups.length > 0 && (
          <div className="p-2">
            <div>
              <select className="form-control" value={selectedGroup} onChange={handleGroupChange}>
                <option value="">Select group...</option>
                {groups.map((group, index) => (
                  <option key={index} value={group.id}>{group.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className="p-2">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <a className={`nav-link ${activeTab === "locations" ? "active" : ""}`} href="javascript:void(0)" onClick={() => setActiveTab("locations")}>Locations ({mapLocations ? mapLocations.length : 0})</a>
            </li>
            <li className="nav-item">
              <a className={`nav-link ${activeTab === "jobs" ? "active" : ""}`} href="javascript:void(0)" onClick={() => setActiveTab("jobs")}>Jobs ({mapJobs ? mapJobs.length : 0})</a>
            </li>
          </ul>
        </div>
        {activeTab === "locations" && mapLocations && <div className="list">
          {mapLocations.map((location, index) => (
            <a key={index} className={`list-item ${selectedLocation && selectedLocation.id === location.id ? "bg-light" : ""}`} href="javascript:void(0)" onClick={() => selectLocation(location)}>
              <strong>{location.name}</strong>
            </a>
          ))}
        </div>}
        {activeTab === "jobs" && mapJobs && <div className="list">
          {mapJobs.map((job, index) => (
            <a key={index} className={`list-item ${selectedJob && selectedJob.id === job.id ? "bg-light" : ""}`} href="javascript:void(0)" onClick={() => selectJob(job)}>
              <strong>{job.task_name}</strong>
              {job.location_name && <div className="small">{job.location_name}</div>}
            </a>
          ))}
        </div>}
      </div>
      <div className="map-container">
        {mapLocations && <Map
          ref={mapRef}
          {...mapViewState}
          scrollZoom={false}
          onMove={evt => setMapViewState(evt.viewState)}
          onLoad={fitToMarkers}
          mapStyle={mapboxStyle}
          mapboxAccessToken={mapboxAccessToken}
        >
          <NavigationControl />
          {renderMarkers()}
          {renderJobMarkers()}
          {selectedLocation && (
            <Popup
              longitude={selectedLocation.longitude}
              latitude={selectedLocation.latitude}
              anchor="bottom"
              offset={40}
              onClose={clearSelection}
            >
              <div><b>{selectedLocation.name}</b></div>
              {selectedLocation.address && (selectedLocation.name !== selectedLocation.address) && (
                <div>{selectedLocation.address}</div>
              )}
            </Popup>
          )}
          {selectedJob && (
            <Popup
              longitude={selectedJob.longitude}
              latitude={selectedJob.latitude}
              anchor="top"
              offset={2}
              onClose={clearSelection}
            >
              <div><b>{selectedJob.task_name}</b></div>
              {selectedJob.location_name && (
                <div>{selectedJob.location_name}</div>
              )}
              <div>{selectedJob.completed_at}</div>
            </Popup>
          )}
        </Map>}
      </div>
    </div>
  );
};

CompanyMap.propTypes = {
  account_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  company_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  completed_dates: PropTypes.array,
  groups: PropTypes.array
};

export default CompanyMap;
